/**
 * Read `manifest.yaml` out of a submission .zip, in the browser, before upload.
 *
 * The point is a single question: is this the practice zip or the graded one?
 * They are byte-identical apart from two lines, they are built minutes apart,
 * and getting it wrong costs a submission that cannot be handed back once the
 * run starts. The answer is inside the file the user already selected, so
 * there is no reason to make them find out from the server.
 *
 * No zip dependency. The app ships none, and pulling one in to read ~200 bytes
 * would be the largest thing in the bundle for the smallest reason. This walks
 * the central directory directly and inflates with the platform's own
 * DecompressionStream.
 *
 * Everything here fails soft and returns null: this is a courtesy label, and
 * the real gate is the server's validator. A zip shape we cannot parse must
 * never block an upload that would otherwise have been accepted.
 */

export interface SubmissionManifest {
  /** 'unknown' when the zip could not be read or declares something else.
   *  Never guessed: the whole point of this file is that mislabelling a
   *  graded zip as practice costs a submission that cannot be refunded. */
  mode: 'graded' | 'practice' | 'unknown';
  runs: number | null;
  submissionName: string | null;
}

// Not in this project's TS lib yet, though every browser we support ships it.
// Declared rather than upgrading the lib target, and guarded at the call site
// so an engine without it degrades to "no label" instead of throwing.
declare const DecompressionStream: {
  new (format: string): ReadableWritablePair<Uint8Array, Uint8Array>;
} | undefined;

const EOCD_SIG = 0x06054b50;
const CD_SIG = 0x02014b50;
// A zip comment can be 64 KiB; the end-of-central-directory record sits just
// before it, so that is how far back we have to be willing to look.
const MAX_EOCD_SCAN = 66 * 1024;

const decoder = new TextDecoder();

function findEocd(view: DataView): number | null {
  const start = Math.max(0, view.byteLength - MAX_EOCD_SCAN);
  for (let i = view.byteLength - 22; i >= start; i -= 1) {
    if (view.getUint32(i, true) === EOCD_SIG) return i;
  }
  return null;
}

/** The raw bytes of one named entry, or null if it is not in the archive. */
async function readEntry(buf: ArrayBuffer, wanted: string): Promise<Uint8Array | null> {
  const view = new DataView(buf);
  const eocd = findEocd(view);
  if (eocd == null) return null;

  const entries = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);

  for (let i = 0; i < entries; i += 1) {
    if (offset + 46 > view.byteLength || view.getUint32(offset, true) !== CD_SIG) return null;
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLen = view.getUint16(offset + 28, true);
    const extraLen = view.getUint16(offset + 30, true);
    const commentLen = view.getUint16(offset + 32, true);
    const localOffset = view.getUint32(offset + 42, true);
    const name = decoder.decode(new Uint8Array(buf, offset + 46, nameLen));

    // Accept a nested manifest too. `zip -r` from a parent directory, and
    // Finder's Compress, both produce `bundle/manifest.yaml`, which is
    // exactly the participant most likely to confuse the two modes.
    if (name === wanted || name.toLowerCase().endsWith(`/${wanted}`)) {
      // The local header repeats the name and extra fields, and its extra
      // field length routinely differs from the central directory's -- so the
      // data offset must be read from the local header, never computed from
      // the central one.
      const localNameLen = view.getUint16(localOffset + 26, true);
      const localExtraLen = view.getUint16(localOffset + 28, true);
      const dataAt = localOffset + 30 + localNameLen + localExtraLen;
      const data = new Uint8Array(buf, dataAt, compressedSize);
      if (method === 0) return data;          // stored
      if (method !== 8) return null;          // anything exotic: give up quietly
      if (typeof DecompressionStream === 'undefined') return null;
      const stream = new Blob([data]).stream()
        .pipeThrough(new DecompressionStream('deflate-raw'));
      return new Uint8Array(await new Response(stream).arrayBuffer());
    }
    offset += 46 + nameLen + extraLen + commentLen;
  }
  return null;
}

/**
 * Parse the two fields that decide what an upload costs.
 *
 * A deliberately small regex parse rather than a YAML dependency: the manifest
 * is validated properly server-side, and all this has to do is read two
 * top-level scalars well enough to label a button.
 */
export async function readSubmissionManifest(file: File): Promise<SubmissionManifest | null> {
  try {
    if (!file.name.toLowerCase().endsWith('.zip')) return null;
    // Cap rather than read: a large bundle on a phone would freeze or OOM
    // the tab on file-select, before the participant has clicked anything.
    if (file.size > 128 * 1024 * 1024) return null;
    const bytes = await readEntry(await file.arrayBuffer(), 'manifest.yaml');
    if (!bytes) return null;
    const text = decoder.decode(bytes);
    const scalar = (key: string): string | null => {
      const m = text.match(new RegExp(`^${key}\\s*:\\s*["']?([^"'#\\n\\r]+)`, 'm'));
      return m ? m[1].trim() : null;
    };
    const runs = scalar('runs');
    const parsedRuns = runs != null && /^\d+$/.test(runs) ? Number(runs) : null;
    const raw = scalar('mode')?.toLowerCase();
    return {
      mode: raw === 'graded' ? 'graded' : raw === 'practice' ? 'practice' : 'unknown',
      runs: parsedRuns,
      submissionName: scalar('submission_name'),
    };
  } catch {
    return null;   // never let a courtesy label break an upload
  }
}
